import { createContext, useContext, useEffect, useState } from "react";
import { Download, Share, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PwaContext = createContext(null);
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
const isAppleMobile = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const useInstall = () => {
  const [installed, setInstalled] = useState(standalone);
  const [prompt, setPrompt] = useState(null);
  const [help, setHelp] = useState(false);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    const media = window.matchMedia("(display-mode: standalone)");
    const capturePrompt = (event) => { event.preventDefault(); setPrompt(event); };
    const completed = () => { setInstalled(true); setPrompt(null); setHelp(false); };
    const changed = () => setInstalled(standalone());
    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener("appinstalled", completed);
    media.addEventListener("change", changed);
    return () => { window.removeEventListener("beforeinstallprompt", capturePrompt); window.removeEventListener("appinstalled", completed); media.removeEventListener("change", changed); };
  }, []);
  const install = async () => {
    if (pending) return;
    setMessage("");
    if (!prompt) { setHelp(true); return; }
    setPending(true);
    try { await prompt.prompt(); await prompt.userChoice; }
    catch { setMessage("The browser could not open its install prompt. You can use its menu instead."); setHelp(true); }
    finally { setPrompt(null); setPending(false); }
  };
  return { installed, help, setHelp, pending, message, install };
};

const InstallHelp = ({ state }) => <Dialog open={state.help} onOpenChange={state.setHelp}>
  <DialogContent data-testid="pwa-install-help" className="w-[calc(100%-2rem)] max-w-md rounded-3xl font-['Figtree']">
    <DialogHeader className="text-left pr-5">
      <DialogTitle data-testid="pwa-install-help-title" className="font-['Nunito'] text-xl">Install Pikyourtrip</DialogTitle>
      <DialogDescription data-testid="pwa-install-help-description">Keep the app on your home screen. Only app files are stored for offline loading; your travel data stays on the server.</DialogDescription>
    </DialogHeader>
    {state.message && <p data-testid="pwa-install-message" role="status" className="text-sm text-stone-600">{state.message}</p>}
    {isAppleMobile() ? <ol data-testid="pwa-ios-instructions" className="list-decimal space-y-3 pl-5 text-sm text-stone-700">
      <li>Open this page in Safari.</li>
      <li>Tap <Share aria-hidden="true" className="mx-1 inline h-4 w-4" /><strong>Share</strong>, then <strong>Add to Home Screen</strong>.</li>
      <li>Keep <strong>Open as Web App</strong> on if shown, then tap <strong>Add</strong>.</li>
    </ol> : <div data-testid="pwa-browser-instructions" className="space-y-3 text-sm text-stone-700">
      <p>Open your browser’s menu and choose <strong>Install Pikyourtrip</strong>, <strong>Install app</strong>, or <strong>Add to Home Screen</strong>, if available.</p>
      <p className="text-stone-500">If there is no install option, open this page in a supported browser such as Chrome or Edge. Already installed? Open Pikyourtrip from your home screen or app launcher.</p>
    </div>}
    <DialogClose asChild><Button data-testid="pwa-install-help-done" className="rounded-full bg-ac hover:bg-ac-hover font-bold">Got it</Button></DialogClose>
  </DialogContent>
</Dialog>;

const OfflineShell = ({ online, checkConnection }) => {
  const [message, setMessage] = useState("");
  return <Dialog open={!online} onOpenChange={() => {}}>
    <DialogContent data-testid="pwa-offline-shell" className="left-0 top-0 right-0 bottom-0 z-[200] h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-none border-0 bg-stone-50 p-0 shadow-none font-['Figtree'] data-[state=open]:!animate-none data-[state=closed]:!animate-none [&>button:last-child]:hidden" onEscapeKeyDown={(event) => event.preventDefault()} onPointerDownOutside={(event) => event.preventDefault()}>
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col px-6 py-6 sm:px-10 sm:py-8">
        <header className="flex items-center gap-3"><img data-testid="pwa-offline-icon" src="/pwa/icon-192.png" alt="" className="h-11 w-11 rounded-xl" /><span data-testid="pwa-offline-brand" className="font-['Nunito'] text-2xl font-bold text-stone-900">Pikyourtrip</span></header>
        <div className="my-auto max-w-xl py-12 sm:py-20">
          <WifiOff aria-hidden="true" className="mb-6 h-9 w-9 text-teal-700" />
          <DialogTitle asChild className="font-['Nunito'] text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900"><h1 data-testid="pwa-offline-title">You’re offline</h1></DialogTitle>
          <DialogDescription data-testid="pwa-offline-description" className="mt-5 text-sm sm:text-base leading-relaxed text-stone-600">The app shell is ready. Reconnect to view or edit your trips, saved places, and lists. No personal data is stored for offline use.</DialogDescription>
          <ul data-testid="pwa-offline-sections" className="my-7 grid grid-cols-2 gap-x-5 gap-y-3 border-y border-stone-200 py-5 text-sm font-semibold text-stone-400">{["Bucket List", "Itinerary", "My Saves", "Packing Lists"].map((label, index) => <li key={label} data-testid={`pwa-offline-section-${index}`}>{label}</li>)}</ul>
          <Button data-testid="pwa-check-connection" onClick={() => { if (!checkConnection()) setMessage("Still offline. Check your connection and try again."); }} className="rounded-full bg-teal-700 hover:bg-teal-800 font-bold">Check connection</Button>
          {message && <p data-testid="pwa-offline-status" role="status" className="mt-3 text-sm text-stone-500">{message}</p>}
        </div>
        <p data-testid="pwa-offline-privacy" className="text-xs text-stone-400">Your saved content remains in your account on the server.</p>
      </div>
    </DialogContent>
  </Dialog>;
};

export const PwaProvider = ({ children }) => {
  const install = useInstall();
  const [online, setOnline] = useState(navigator.onLine);
  const [hasConnected, setHasConnected] = useState(navigator.onLine);
  const checkConnection = () => { const connected = navigator.onLine; setOnline(connected); if (connected) setHasConnected(true); return connected; };
  useEffect(() => {
    const connected = () => { setOnline(true); setHasConnected(true); };
    const disconnected = () => setOnline(false);
    window.addEventListener("online", connected); window.addEventListener("offline", disconnected);
    return () => { window.removeEventListener("online", connected); window.removeEventListener("offline", disconnected); };
  }, []);
  return <PwaContext.Provider value={install}>
    {hasConnected && children}
    <InstallHelp state={install} />
    <OfflineShell online={online} checkConnection={checkConnection} />
  </PwaContext.Provider>;
};

export const InstallAppButton = ({ transparent }) => {
  const state = useContext(PwaContext);
  if (state.installed) return null;
  return <Button type="button" data-testid="pwa-install-button" aria-label="Install Pikyourtrip" title="Install Pikyourtrip" disabled={state.pending} onClick={state.install} variant="outline" className={`h-9 shrink-0 gap-1.5 rounded-full px-3 font-bold ${transparent ? "border-white/50 bg-white/10 text-white hover:bg-white/20 hover:text-white" : "border-stone-200 bg-white text-stone-700 hover:bg-stone-100"}`}><Download aria-hidden="true" className="h-4 w-4" /><span>Install</span></Button>;
};
